import './assets/main.css'
import 'primeflex/primeflex.css'
// primeicons already bundled via PrimeVue components if needed; avoid duplicate import
import './assets/theme.css'
import './assets/css/animations.css'
import './assets/css/flags.css'

import firebase from 'firebase/compat/app'
import 'firebase/compat/auth'
import '@/firebase/init.js'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import PrimeVue from 'primevue/config'
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'
import Toast from 'primevue/toast'
import Password from 'primevue/password'
import Calendar from 'primevue/calendar'
import Checkbox from 'primevue/checkbox'
import BadgeDirective from 'primevue/badgedirective'
import { registerSW } from 'virtual:pwa-register'
import { usePwaStore } from '@/stores/pwa.store.js'
import { i18n, locale as initialLocale, loadLocaleResources } from '@/i18n.js'
import {
    forceReloadDueToStaleAssets,
    isStaleModuleImportError,
} from '@/utils/stale-client.js'
import { setupFirstActivityTracking, initMixpanel } from '@/utils/tracking.js'

const PWA_PROMPT_EVENT_KEY = '__pwaDeferredInstallPrompt__'
const PWA_PROMPT_HANDLER_KEY = '__pwaInstallPromptListener__'
const SW_UPDATE_INTERVAL_KEY = '__swUpdateIntervalId__'
const LOCAL_VERSION_KEY = 'app-version'


if (typeof window !== 'undefined') {
    // Centralized stale-asset handler. We attach once per load to capture any
    // chunk import failures bubbling up as window errors or promise rejections.
    // When detected, the user is forced through the stale-client recovery flow.
    const STALE_HANDLER_KEY = '__staleChunkReloadHandlers__'

    const detachExistingHandlers = () => {
        // Avoid stacking duplicate listeners during hot reload / reuse.
        const existing = window[STALE_HANDLER_KEY]
        if (existing && typeof existing === 'object') {
            try {
                existing.error &&
                    window.removeEventListener('error', existing.error)
                existing.rejection &&
                    window.removeEventListener(
                        'unhandledrejection',
                        existing.rejection
                    )
            } catch (_) {}
        }
    }

    const extractError = (event) => {
        if (!event) return null
        if (typeof event.reason !== 'undefined') {
            return event.reason
        }
        if (typeof event.error !== 'undefined') {
            return event.error
        }
        if (typeof event.message === 'string') {
            return event.message
        }
        return null
    }

    const handleStaleError = (source, value) => {
        if (value && isStaleModuleImportError(value)) {
            // Defer the heavy lifting to stale-client; we only need the reason.
            forceReloadDueToStaleAssets({ reason: source })
        }
    }

    detachExistingHandlers()

    const onWindowError = (event) => {
        const value = extractError(event)
        handleStaleError('window-error', value ?? event)
    }

    const onUnhandledRejection = (event) => {
        const value = extractError(event)
        handleStaleError('window-unhandledrejection', value ?? event)
    }

    try {
        window.addEventListener('error', onWindowError)
        window.addEventListener('unhandledrejection', onUnhandledRejection)
        window[STALE_HANDLER_KEY] = {
            error: onWindowError,
            rejection: onUnhandledRejection,
        }
    } catch (_) {}


}

if (typeof window !== 'undefined') {
    const deferredPromptHandler = (event) => {
        console.log('beforeinstallprompt captured early', event)
        event.preventDefault()
        window[PWA_PROMPT_EVENT_KEY] = event
        window.dispatchEvent(new CustomEvent('pwa:install-prompt-available'))
    }

    if (window[PWA_PROMPT_HANDLER_KEY]) {
        window.removeEventListener(
            'beforeinstallprompt',
            window[PWA_PROMPT_HANDLER_KEY]
        )
    }

    window[PWA_PROMPT_HANDLER_KEY] = deferredPromptHandler
    window.addEventListener('beforeinstallprompt', deferredPromptHandler)
}



// Lazy-load Sentry so the initial bundle does not pull in tracing/replay code by default
const initSentry = async (app) => {
    try {
        const Sentry = await import('@sentry/vue')

        Sentry.init({
            app,
            dsn: 'https://3d37843632fd0a586853f5fe4373b47f@o4509922301706240.ingest.us.sentry.io/4509922304458752',
            sendDefaultPii: true,
            integrations: [
                Sentry.browserTracingIntegration({ router }),
                Sentry.replayIntegration(),
            ],
            tracesSampleRate: 1.0,
            tracePropagationTargets: ['localhost', /^https:\/\/api.wispal\.me/],
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0,
            enableLogs: true,
        })
    } catch (error) {
        console.error('Failed to initialize Sentry', error)
    }
}

import App from './App.vue'
import router from './router'

async function checkVersionMismatch() {
    try {
        const response = await fetch('/version.json?t=' + Date.now(), {
            cache: 'no-store',
        })

        if (!response.ok) {
            console.warn('Could not fetch version.json, skipping cache check.')
            return null
        }

        const serverVersionData = await response.json()
        const serverVersion = serverVersionData?.sha

        if (!serverVersion) {
            console.warn('Fetched version.json without a sha field.')
            return null
        }

        const localVersion = localStorage.getItem(LOCAL_VERSION_KEY)

        if (!localVersion) {
            localStorage.setItem(LOCAL_VERSION_KEY, serverVersion)
            return null
        }

        if (localVersion === serverVersion) {
            return null
        }

        return {
            serverVersion,
            localVersion,
        }
    } catch (error) {
        console.error('Failed to check version and clear cache:', error)
        return null
    }
}

// Clear cache on version mismatch without blocking first paint
;(async () => {
    let versionMismatch = null
    try {
        versionMismatch = await checkVersionMismatch()
    } catch (error) {
        console.error('Initial version check failed:', error)
    }

    if (versionMismatch) {
        await forceReloadDueToStaleAssets({
            reason: 'version-mismatch',
            serverVersion: versionMismatch.serverVersion,
            versionStorageKey: LOCAL_VERSION_KEY,
        })
        return
    }

    const localeReadyPromise = loadLocaleResources(initialLocale).catch(
        (error) => {
            console.error('Failed to load locale resources on startup:', error)
        }
    )

    const appReadyPromise = (async () => {
        await localeReadyPromise
        return initializeApp()
    })()

    if (typeof window !== 'undefined') {
        requestAnimationFrame(() => {
            initMixpanel().then(() => {
                setupFirstActivityTracking()
            })
        })
    }

    appReadyPromise.catch((error) => {
        console.error('Failed to initialize the application:', error)
    })
})()

async function initializeApp() {
    const app = createApp(App)

    app.config.devtools = import.meta.env.VITE_APP_DEVTOOLS === 'true'

    const pinia = createPinia()
    pinia.use(piniaPluginPersistedstate)

    app.use(pinia)

    const pwa = usePwaStore()

    const syncDeferredInstallPrompt = () => {
        if (typeof window === 'undefined') return
        const pendingEvent = window[PWA_PROMPT_EVENT_KEY]
        if (pendingEvent) {
            pwa.setInstallPrompt(pendingEvent)
        }
    }

    if (typeof window !== 'undefined') {
        syncDeferredInstallPrompt()
        window.removeEventListener(
            'pwa:install-prompt-available',
            syncDeferredInstallPrompt
        )
        window.addEventListener(
            'pwa:install-prompt-available',
            syncDeferredInstallPrompt
        )
    }

    const updateSW = registerSW({
        onNeedRefresh() {
            pwa.setUpdate(updateSW)
        },
        onOfflineReady() {
            console.log('App is ready to work offline')
        },
        onRegistered(r) {
            if (r) {
                // Clear any existing interval to prevent duplicates during hot reload
                if (window[SW_UPDATE_INTERVAL_KEY]) {
                    clearInterval(window[SW_UPDATE_INTERVAL_KEY])
                }
                window[SW_UPDATE_INTERVAL_KEY] = setInterval(
                    () => {
                        console.log('Checking for sw update')
                        r.update()
                    },
                    60 * 60 * 1000
                )
            }
        },
    })

    window.addEventListener('appinstalled', () => {
        pwa.setInstalled(true)
    })

    // Detect initial display-mode
    const checkInstalled = () => {
        if (
            window.matchMedia('(display-mode: standalone)').matches ||
            navigator.standalone
        ) {
            pwa.setInstalled(true)
        }
    }
    checkInstalled()
    window
        .matchMedia('(display-mode: standalone)')
        .addEventListener('change', (e) => {
            if (e.matches) {
                pwa.setInstalled(true)
            }
        })

    app.use(router)
    app.use(PrimeVue)
    app.use(ToastService)
    app.use(ConfirmationService)
    app.use(i18n)
    app.component('InputText', InputText)
    app.component('Button', Button)
    app.component('Toast', Toast)
    app.component('Password', Password)
    app.component('Calendar', Calendar)
    app.component('Checkbox', Checkbox)
    app.directive('badge', BadgeDirective)

    const routerReady = router.isReady()
    const sentryReady = initSentry(app)
    await Promise.all([routerReady, sentryReady])
    app.mount('#app')
    return app
}
